import { StyleProp } from '@mantine/core';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Link, Text } from '@zorro/web/design-system';
import { useTextOverflow } from '@zorro/web/ui-utils';
import React from 'react';

type Props = {
  text: string;
  link?: string | null;
  size?: 'xs' | 'sm';
  color?: string;
  fw?: number;
  maw?: StyleProp<React.CSSProperties['maxWidth']>;
  lines?: number;
  onClick?: () => void;
};

const SINGLE_LINE_STYLE: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const MULTI_LINE_ANCHOR_STYLE: React.CSSProperties = {
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
};

export const TextWithLink = ({
  text,
  link,
  size = 'sm',
  color,
  fw,
  maw,
  lines,
  onClick,
}: Props) => {
  const { isTextOverflowing, textRef } = useTextOverflow(text);

  const textElement = (
    <Tooltip label={text} position="top" disabled={!isTextOverflowing}>
      <Text
        ref={textRef}
        size={size}
        c={color}
        fw={fw}
        pr="0.25rem"
        maw={maw}
        lineClamp={lines}
        display={lines ? '-webkit-box' : undefined}
        style={lines ? { minHeight: `${lines}lh` } : SINGLE_LINE_STYLE}
      >
        {text}
      </Text>
    </Tooltip>
  );

  return link ? (
    <Link
      href={link}
      target="_blank"
      anchorProps={{
        onClick: (event) => {
          event.stopPropagation();
          onClick?.();
        },
        fw: 600,
        // eslint-disable-next-line id-length
        c: 'zorroCoal.9',
        style: lines ? MULTI_LINE_ANCHOR_STYLE : { alignItems: 'center' },
        rel: 'noopener noreferrer',
        externalLinkSize: '1rem',
      }}
    >
      {textElement}
    </Link>
  ) : (
    textElement
  );
};
